import { Box, CircularProgress, Typography } from '@mui/material';
import { LinearProgressWithLabel } from 'components/LinearProgressWithLabel';
import theme from 'core/theme';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

interface SmartRecommendationLoadingProps {
  startTime?: string;
  intervalInSeconds?: number;
}

export const SmartRecommendationLoading: FC<SmartRecommendationLoadingProps> = ({ startTime, intervalInSeconds }) => {
  const [timeRemaining, setTimeRemaining] = useState<number>(() => {
    if (startTime && intervalInSeconds) {
      return intervalInSeconds - Math.floor((Date.now() - new Date(startTime).getTime()) / 1000);
    }
    return 0;
  });
  const [percentage, setPercentage] = useState<number>(() => {
    if (startTime && intervalInSeconds) {
      const percentageCompleted = intervalInSeconds - timeRemaining;
      return Math.floor((percentageCompleted / intervalInSeconds) * 100);
    }
    return 1;
  });

  const showRemainingTime = useMemo(() => startTime && intervalInSeconds, [startTime, intervalInSeconds]);

  const updateTimeRemaining = useCallback(() => {
    console.log('updating time...');
    if (showRemainingTime && startTime && intervalInSeconds) {
      const timeRemainingTmp = intervalInSeconds - Math.floor((Date.now() - new Date(startTime).getTime()) / 1000);
      setTimeRemaining(Math.max(timeRemainingTmp, 0));
      const percentageCompleted = intervalInSeconds - timeRemainingTmp;
      setPercentage(Math.max(Math.floor((percentageCompleted / intervalInSeconds) * 100), 1));
    }
  }, [intervalInSeconds, showRemainingTime, startTime]);

  useEffect(() => {
    if (showRemainingTime) {
      const interval = setInterval(updateTimeRemaining, 1000);
      return () => interval && clearInterval(interval);
    }
  });

  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
        }}
      >
        <Box
          flex={10}
          sx={{
            border: `1px dotted ${theme.palette.primary.main}`,
            borderRight: `1px solid ${theme.palette.primary.main}`,
            borderRadius: '5px 0px 0px 5px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="p16" sx={{ color: theme.palette.neutral.darkGray }}>
            Searching recommendation...
          </Typography>
        </Box>
        <Box
          flex={1}
          sx={{
            backgroundColor: theme.palette.primary.main,
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: '0px 5px 5px 0px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress sx={{ color: theme.palette.neutral.white }} size={13} />
        </Box>
      </Box>
      {showRemainingTime && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box>
              <Typography variant="p14" sx={{ color: theme.palette.neutral.darkGray }}>
                Approximate time remaining {minutes}m {seconds}s
              </Typography>
            </Box>
          </Box>
          <Box>
            <LinearProgressWithLabel
              variant="determinate"
              color="primary"
              value={percentage || 100}
            ></LinearProgressWithLabel>
          </Box>
        </>
      )}
    </Box>
  );
};
